import { useState } from "react";
import { Mail, Phone, MapPin, Globe, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "../lib/api";
import { CONTACT } from "../data/apps";
import { Reveal } from "../components/Shared";

const INFOS = [
  { icon: Mail, label: "E-mail", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Phone, label: "Téléphone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.whatsapp, href: CONTACT.whatsappLink },
  { icon: Globe, label: "Site", value: CONTACT.site, href: "#" },
  { icon: MapPin, label: "Adresse", value: CONTACT.address, href: "#" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendContact(form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-[70px]">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 lg:pt-24 pb-24">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact</div>
              <h1 className="mt-4 font-display font-semibold tracking-tight text-navy text-4xl sm:text-5xl">
                Parlons de votre projet.
              </h1>
              <p className="mt-5 max-w-md text-muted-foreground leading-relaxed">
                Une question sur nos applications, un partenariat ou une opportunité ?
                Écrivez-nous, l'équipe TIRERA GROUP vous répond depuis Bamako.
              </p>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {INFOS.map((info) => {
                  const Icon = info.icon;
                  return (
                    <a
                      key={info.label}
                      href={info.href}
                      data-testid={`contact-info-${info.label.toLowerCase()}`}
                      className="flex items-center gap-4 rounded-2xl border border-border p-5 hover:border-navy/30 transition-colors"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-navy">
                        <Icon size={18} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">{info.label}</div>
                        <div className="font-medium text-navy truncate">{info.value}</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </Reveal>

          <Reveal i={1}>
            <div className="rounded-3xl border border-border p-7 sm:p-9 bg-secondary/40">
              {sent ? (
                <div data-testid="contact-success" className="flex flex-col items-center justify-center text-center py-16">
                  <CheckCircle2 size={48} className="text-africain" />
                  <h3 className="mt-5 font-display font-semibold text-xl text-navy">Message envoyé !</h3>
                  <p className="mt-2 text-muted-foreground max-w-xs">Merci de nous avoir contactés. Nous reviendrons vers vous rapidement.</p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-new-message"
                    className="mt-6 rounded-full border border-border bg-white px-6 py-2.5 text-sm font-semibold text-navy hover:border-navy/40 transition-colors"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Nom complet" testid="contact-name">
                      <input required value={form.name} onChange={update("name")} data-testid="contact-name-input" className="tg-input" placeholder="Votre nom" />
                    </Field>
                    <Field label="E-mail" testid="contact-email">
                      <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" className="tg-input" placeholder="vous@exemple.com" />
                    </Field>
                  </div>
                  <Field label="Sujet" testid="contact-subject">
                    <input value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" className="tg-input" placeholder="Sujet de votre message" />
                  </Field>
                  <Field label="Message" testid="contact-message">
                    <textarea required rows={5} value={form.message} onChange={update("message")} data-testid="contact-message-input" className="tg-input resize-none" placeholder="Votre message…" />
                  </Field>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white hover:bg-navy-600 transition-colors disabled:opacity-60"
                  >
                    {loading ? "Envoi…" : <>Envoyer le message <Send size={16} /></>}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-navy">{label}</span>
    {children}
  </label>
);
